import SectionHeading from './SectionHeading';
import { rankItems, timelineItems, topicTags } from '@/mocks/home';

export default function LiveTimeline() {
  return (
    <section id="stream" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-shell px-4 md:px-6">
        <SectionHeading
          eyebrow="LIVE TIMELINE"
          title="实时快讯流"
          desc="按时间顺序排好的今日动态，刚刚发生的每一条都在这里，往下刷越密。"
          action={{ label: '查看全部快讯', href: '#stream' }}
        />

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
          <div className="relative">
            <div className="absolute left-[76px] top-3 bottom-3 hidden w-px bg-background-200 md:block"></div>

            <div className="flex flex-col gap-5">
              {timelineItems.map((item) => (
                <div key={item.id} className="relative flex gap-4 md:gap-5">
                  <div className="hidden w-12 shrink-0 pt-2 text-right md:block">
                    <span className="text-sm font-medium tabular-nums text-foreground-950">{item.time}</span>
                  </div>
                  <div className="relative hidden w-4 shrink-0 justify-center pt-3 md:flex">
                    <span
                      className={`relative z-10 h-2.5 w-2.5 rounded-full ring-4 ring-background-50 ${
                        item.hot ? 'bg-primary-500' : 'bg-background-400'
                      }`}
                    ></span>
                  </div>

                  <article className="group flex-1 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5">
                    <div className="flex items-center gap-2">
                      <span className="md:hidden text-xs font-medium tabular-nums text-foreground-500">{item.time}</span>
                      <span
                        className={`rounded-sm px-2 py-0.5 text-[11px] font-medium ${
                          item.hot ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-800'
                        }`}
                      >
                        {item.tag}
                      </span>
                      {item.hot && (
                        <span className="flex items-center gap-1 text-[11px] text-accent-700">
                          <i className="ri-fire-fill"></i>
                          热议中
                        </span>
                      )}
                    </div>

                    <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="flex-1">
                        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>
                      </div>
                      <div className="h-28 w-full shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-24 sm:w-36">
                        <img
                          src={item.image}
                          alt={item.title}
                          title={`${item.title} - 免费看料`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-5 border-t border-background-200/70 pt-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-chat-3-line text-sm"></i>
                        {item.comments} 条讨论
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-thumb-up-line text-sm"></i>
                        {item.likes}
                      </span>
                      <a
                        href="#discuss"
                        className="ml-auto flex items-center gap-1 text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
                      >
                        去评论区
                        <i className="ri-arrow-right-s-line"></i>
                      </a>
                    </div>
                  </article>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-center">
              <a
                href="#stream"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                加载更早的实时快讯
              </a>
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5 lg:sticky lg:top-20">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-bold text-foreground-950">实时热议榜</h3>
                <span className="text-[11px] text-foreground-500">每分钟更新</span>
              </div>
              <ol className="mt-4 flex flex-col gap-3">
                {rankItems.map((item, idx) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        idx < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground-900">{item.title}</p>
                      <p className="mt-1 flex items-center gap-2 text-[11px] text-foreground-500">
                        <span>{item.heat} 热度</span>
                        <span className={item.up ? 'text-secondary-600' : 'text-foreground-400'}>
                          <i className={item.up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
                          {item.trend}
                        </span>
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">正在讨论的话题</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {topicTags.slice(0, 6).map((tag) => (
                  <a
                    key={tag.id}
                    href="#topics"
                    className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1.5 text-xs text-secondary-800 transition-colors hover:bg-secondary-100 cursor-pointer"
                  >
                    #{tag.label}
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}